import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Tooltip,
  TablePagination,
  Box
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { AppLabels } from '../../constant/constant';
import {
  DeviceTroubleHistoryPanel,
  DeviceTroubleHistoryHeader,
  DeviceTroubleHistoryTableContainer,
  DeviceTroubleHistoryMessage,
  DeviceTroubleSecondaryButton
} from './DeviceTrouble.styles';
import DateUtils from '../../utils/DateUtils.jsx';
import { formattedText } from '../../utils/TextTooltipUtils.jsx';

/**
 * DeviceHistoryPanel Component - Displays device history data in a table format
 * 
 * Features:
 * - Shows device history with date, action, and comments
 * - Loading and error states
 * - Closeable panel
 * - Responsive table layout
 * - Refresh button to reload latest data
 * - Table pagination for large datasets
 * 
 * @param {Array} historyData - Array of history records
 * @param {boolean} loading - Loading state for history data
 * @param {string} error - Error message if history loading fails
 * @param {Function} onClose - Callback to close the history panel
 * @param {Function} onRefresh - Callback to refresh history data
 * @param {string} deviceMac - Device MAC address for display
 */
const DeviceHistoryPanel = ({
  historyData,
  loading,
  error,
  onClose,
  onRefresh,
  deviceMac
}) => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(AppLabels.deviceTroubleLabels.DEFAULT_ROWS_PER_PAGE);
  const [refreshing, setRefreshing] = useState(false);

  // Reset pagination when data changes
  useEffect(() => {
    setPage(0);
  }, [historyData]);

  /**
   * Handle refresh button click
   */
  const handleRefresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);

    try {
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Error refreshing history:', err);
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, AppLabels.deviceTroubleLabels.DEFAULT_ROWS_PER_PAGE));
    setPage(0);
  };

  // Calculate paginated data
  const paginatedData = historyData ? historyData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];

  return (
    <DeviceTroubleHistoryPanel>
      {/* History Panel Header */}
      <DeviceTroubleHistoryHeader>
        <Typography sx={{ fontSize: '1rem', margin: 0 }} className='title'>
          {AppLabels.deviceTroubleLabels.historyPanelTitle} - {deviceMac}
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Tooltip title="Refresh History" arrow>
            <DeviceTroubleSecondaryButton
              size="small"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Refresh
            </DeviceTroubleSecondaryButton>
          </Tooltip>
          <Tooltip title={AppLabels.deviceTroubleLabels.closeHistory} arrow>
            <DeviceTroubleSecondaryButton
              size="small"
              onClick={onClose}
            >
              {AppLabels.deviceTroubleLabels.closeHistory}
            </DeviceTroubleSecondaryButton>
          </Tooltip>
        </Box>
      </DeviceTroubleHistoryHeader>

      {/* Loading State */}
      {loading && (
        <DeviceTroubleHistoryMessage>
          <CircularProgress size={20} sx={{ color: '#4f7cff' }} />
          {AppLabels.deviceTroubleLabels.loadingHistory}
        </DeviceTroubleHistoryMessage>
      )}

      {/* Error State */}
      {error && !loading && (
        <DeviceTroubleHistoryMessage>
          <Typography sx={{ color: '#ef4444', fontSize: '24px', marginRight: '8px' }}>⚠️</Typography>
          <div>
            <Typography sx={{ color: '#ef4444', fontWeight: 600, marginBottom: '4px' }}>
              {AppLabels.deviceTroubleLabels.errorLoadingHistory}
            </Typography>
            <Typography sx={{ color: '#ef4444', fontSize: '0.9rem' }}>
              {error}
            </Typography>
          </div>
        </DeviceTroubleHistoryMessage>
      )}

      {/* History Data Table */}
      {!loading && !error && (
        <DeviceTroubleHistoryTableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{AppLabels.deviceTroubleLabels.historyDate}</TableCell>
                <TableCell>{AppLabels.deviceTroubleLabels.historyTriggeredBy} </TableCell>
                <TableCell>{AppLabels.deviceTroubleLabels.historyAction}</TableCell>
                <TableCell>{AppLabels.deviceTroubleLabels.historyStatus}</TableCell>
                <TableCell>{AppLabels.deviceTroubleLabels.historyComments}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!historyData || historyData.length === 0 ? (
                /* No History Data State */
                <TableRow>
                  <TableCell colSpan={5}>
                    <DeviceTroubleHistoryMessage>
                      <HistoryIcon sx={{ color: '#9ca3af', fontSize: '24px' }} />
                      {AppLabels.deviceTroubleLabels.noHistoryData}
                    </DeviceTroubleHistoryMessage>
                  </TableCell>
                </TableRow>
              ) : (
                /* History Data Rows - Paginated */
                paginatedData.map((record, index) => (
                  <TableRow key={page * rowsPerPage + index}>
                    <TableCell>
                      {DateUtils.formatDateWithLocalZone(record.createdAt) ?? AppLabels.notApplicable}
                    </TableCell>
                    <TableCell>
                      {record.triggeredBy ?? AppLabels.notApplicable}
                    </TableCell>
                    <TableCell>
                      {formattedText(record.action)}
                    </TableCell>
                    <TableCell>
                      {formattedText(record.status)}
                    </TableCell>
                    <TableCell>
                      {formattedText(record.remarks)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Table Pagination */}
          {historyData && historyData.length > AppLabels.deviceTroubleLabels.DEFAULT_ROWS_PER_PAGE && (
            <TablePagination
              component="div"
              count={historyData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </DeviceTroubleHistoryTableContainer>
      )}
    </DeviceTroubleHistoryPanel>
  );
};

// PropTypes validation
DeviceHistoryPanel.propTypes = {
  historyData: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  deviceMac: PropTypes.string.isRequired
};

DeviceHistoryPanel.defaultProps = {
  historyData: [],
  error: null,
  onRefresh: null
};

export default DeviceHistoryPanel;
